/**
 * LogToolbar - shared toolbar for log filtering and actions
 *
 * Used by ErrorOverlay (global) and NodeInspector (per-node).
 */

import { LogLevel } from '@/logging';
import { LEVEL_COLORS } from './LogList';

const LEVEL_ORDER: LogLevel[] = [LogLevel.Error, LogLevel.Info, LogLevel.Trace];

interface LogToolbarProps {
  enabledLevels: Set<LogLevel>;
  onToggleLevel: (level: LogLevel) => void;
  onClear: () => void;
  onCopy: () => void;
  filteredCount: number;
  totalCount: number;
}

export function LogToolbar({
  enabledLevels,
  onToggleLevel,
  onClear,
  onCopy,
  filteredCount,
  totalCount
}: LogToolbarProps) {
  return (
    <div style={styles.toolbar}>
      <div style={styles.levelButtons}>
        {LEVEL_ORDER.map(level => (
          <button
            key={level}
            onClick={() => onToggleLevel(level)}
            style={{
              ...styles.levelButton,
              background: enabledLevels.has(level) ? LEVEL_COLORS[level] : '#333',
              color: enabledLevels.has(level) ? '#fff' : '#666'
            }}
            title={`${enabledLevels.has(level) ? 'Hide' : 'Show'} ${level} logs`}
          >
            {level.charAt(0).toUpperCase()}
          </button>
        ))}
      </div>

      <span style={styles.count}>{filteredCount}/{totalCount}</span>
      <button onClick={onCopy} style={styles.copyButton} title="Copy logs">Copy</button>
      <button onClick={onClear} style={styles.clearButton}>Clear</button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: '#1f1f1f',
    borderTop: '1px solid #444',
    flexShrink: 0
  },
  levelButtons: {
    display: 'flex',
    gap: 2
  },
  levelButton: {
    border: 'none',
    borderRadius: 3,
    padding: '4px 8px',
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  count: {
    fontSize: 11,
    color: '#9ca3af',
    marginLeft: 'auto'
  },
  copyButton: {
    background: '#374151',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 11,
    cursor: 'pointer'
  },
  clearButton: {
    background: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 11,
    cursor: 'pointer'
  }
};
